import React, { useState } from "react";
import { CalendarClock, AlertTriangle, Package, Boxes, Ban } from "lucide-react";
import { Tile } from "../Primitives";
import { fmtDate } from "../../lib/origin.jsx";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

export function AsOfChip({ valuation, testid = "valuation-as-of" }) {
  return (
    <span data-testid={testid} data-stale={valuation.stale ? "true" : "false"}
          title={valuation.stale ? `Inventory data is ${valuation.ageDays} days old (flagged after 7 days)` : "Date of the latest inventory data update"}
          className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10.5px] font-medium",
            valuation.stale ? "border-warning/50 bg-warning/10 text-warning" : "border-border bg-surface-2 text-muted")}>
      {valuation.stale ? <AlertTriangle size={10} /> : <CalendarClock size={10} />}
      Ending Inventory as of {fmtDate(valuation.asOf)}
      {valuation.stale && <span className="font-mono-tabular">· {valuation.ageDays}d old</span>}
    </span>
  );
}

function Breakdown({ title, rows, testid, renderSub }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, 6);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted">{title}</span>
        <span className="text-[10px] text-subtle font-mono-tabular">{rows.length} total</span>
      </div>
      <div className={cn("space-y-1.5", all && "max-h-[320px] overflow-y-auto pr-1")}>
        {shown.map(r => (
          <div key={r.key} data-testid={`${testid}-row-${r.key}`} className="text-[12px]">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">{r.name}</span>
              <span className="font-mono-tabular shrink-0">{formatPeso(r.value, { compact: true })}
                <span className="text-muted ml-2 w-12 inline-block text-right">{(r.share * 100).toFixed(1)}%</span>
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="h-1 flex-1 bg-surface-2 rounded-sm overflow-hidden">
                <div className={cn("h-full", r.origin === "actual" ? "bg-success" : r.origin === "mixed" ? "bg-info" : "bg-primary/70")}
                     style={{ width: `${Math.max(1, r.share * 100 / (rows[0]?.share || 1))}%` }} />
              </div>
              <span className="text-[10px] text-subtle font-mono-tabular w-28 text-right">{renderSub(r)}</span>
            </div>
          </div>
        ))}
      </div>
      {rows.length > 6 && (
        <button data-testid={`${testid}-toggle`} onClick={() => setAll(a => !a)}
                className="mt-2 text-[11px] text-primary hover:underline">
          {all ? "Show top 6" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

export default function EndingInventoryCard({ valuation, branchName }) {
  const v = valuation;
  return (
    <Tile testid="ending-inventory-card" origin={v.originProps}
          title={<span className="text-[11px] font-semibold tracking-widest uppercase text-muted">Ending Inventory Valuation · historical cost{branchName ? ` · ${branchName}` : ""}</span>}>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-4">
          <div data-testid="ending-inventory-total" className="font-mono-tabular text-4xl md:text-5xl font-semibold text-primary leading-none">
            {formatPeso(v.total, { compact: true })}
          </div>
          <div className="text-[11px] text-muted font-mono-tabular mt-1.5" data-testid="ending-inventory-total-full">{formatPeso(v.total)}</div>
          <div className="mt-3"><AsOfChip valuation={v} /></div>
          <div className="text-[11px] text-muted mt-3 leading-relaxed">On-hand quantity × each item's recorded historical unit cost.</div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <Fig icon={Package} label="Items valued" value={formatNumber(v.itemsValued)} testid="valuation-items" />
            <Fig icon={Boxes} label="Units valued" value={formatNumber(Math.round(v.unitsValued), { compact: true })} testid="valuation-units" />
            <Fig icon={Ban} label="No cost · excluded" value={formatNumber(v.excluded)} testid="valuation-excluded" warn={v.excluded > 0} />
          </div>
          {v.actualReason && (
            <div data-testid="valuation-actual-reason" className="mt-3 text-[10.5px] text-subtle leading-snug">
              Actual 1Rotary valuation unavailable right now: {v.actualReason}
            </div>
          )}
        </div>
        <div className="xl:col-span-4">
          <Breakdown title="By branch" rows={v.byBranch} testid="valuation-by-branch" renderSub={r => `${formatNumber(r.items)} items`} />
        </div>
        <div className="xl:col-span-4">
          <Breakdown title="By category" rows={v.byCategory} testid="valuation-by-category" renderSub={r => `${formatNumber(r.items)} items`} />
        </div>
      </div>
    </Tile>
  );
}

function Fig({ icon: Icon, label, value, testid, warn }) {
  return (
    <div className="border border-border rounded-sm px-2 py-1.5 bg-surface-2/40" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-muted flex items-center gap-1"><Icon size={9} />{label}</div>
      <div className={cn("font-mono-tabular text-sm font-semibold mt-0.5", warn && "text-warning")}>{value}</div>
    </div>
  );
}
